import React, { useMemo } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';
import cx from 'classnames';
import { getSearchParam } from 'helpers/getSearchParam';
import { getFlyingBetslipConfig } from 'helpers/prematch';

import {
  get,
  getConfigProp,
  SelectedSportsbookSportProvider,
  SiteConfigManager,
  useSettings,
} from '@atlas/core';
import { COMPONENT_TYPES, SelectionsRedirect, sportsbookRoutes } from '@atlas/routing';
import { ScrollableContainer } from '@atlas/ui';

import { useLocalBetslip } from 'common/Betslip/LocalBetslipContext';
import { cmdSymbol, searchQuerySymbol } from 'common/constants';
import SportsList from 'common/Widgets/SportsList/SportsList';
import SportsTimeFilter from 'common/Widgets/SportsTimeFilter/SportsTimeFilter';
import TopEvents from 'common/Widgets/TopEvents';
import SportsbookFavorites from 'components/FavoriteEvents/SportsbookFavorites';
import FreeBetNotification from 'components/FreeBetNotification/FreeBetNotification';
import DetailsPage from 'components/Pages/Details';
import EmptySportView from 'components/Pages/EmptySportView';
import SportLoader from 'components/Pages/Prematch/SportLoader';
import { SportsbookBanner } from 'components/Pages/Prematch/SportsbookBanner';
import PrematchSportsbookLoader from 'components/Pages/PrematchSportsbookLoader';
import {
  SelectedEventQueryRouteWrapper,
  SelectedEventQueryViewWrapper,
  SelectedProviderWrapper,
} from 'components/Pages/SelectedWrappers';
import SportItemDestruction from 'components/Pages/SportItemDestruction';
import { DefaultSportsbookNavigation } from 'components/Pages/SportsbookNavigation';
import { CategoryRedirect, SportRedirect } from 'components/Pages/SportsbookRedirects';
import { StandardSearch } from 'components/Search';
import { SelectionProvider } from 'components/SportsbookSelections/SelectionsContext';
import SportsbookSelections from 'components/SportsbookSelections/SportsbookSelections';

import Prematch from './Prematch';
import SelectedTournamentView from './SelectedTournamentView';

const PrematchSportsbookView = () => {
  const { quickBet: isQuickBet } = useSettings();
  const { betIdToEdit } = useLocalBetslip();

  const page = SiteConfigManager.getComponentOptions(COMPONENT_TYPES.SPORT_PREMATCH);
  const sportPrematchPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.SPORT_PREMATCH);
  const isCouponsActive = SiteConfigManager.getConfig('config.config.sportsbook.isCouponsActive');
  const openCouponsByDefault = SiteConfigManager.getConfig(
    'config.config.sportsbook.openCouponsByDefault',
  );
  const isSportRadarProject = getConfigProp('isSportRadarProject');

  const areTopEventsAvailable = get(page, 'topEvents', true);

  const location = useLocation();
  const { pathname } = location;
  const [locationPage, locationSportName, locationCategoryName] = pathname
    .split('/')
    .filter(Boolean);
  const { search } = location;
  const isCmdLink = search.indexOf(cmdSymbol) !== -1;
  const searchValue = getSearchParam(search, searchQuerySymbol);

  const isSearchActive = !!(search && searchValue);

  const layoutToScrollClass = useMemo(
    () =>
      cx('layout-to-scroll full-height', {
        hidden: isSearchActive,
        'layout-to-scroll-with-padding': getFlyingBetslipConfig().hasExtended,
        quickbet: isQuickBet || betIdToEdit,
      }),
    [betIdToEdit, isQuickBet, isSearchActive],
  );

  const isSelectionsRedirect = isCouponsActive && openCouponsByDefault && !isCmdLink;

  const isEnableSearchMobile = React.useMemo(
    () => SiteConfigManager.getConfig('config.config.sportsbook.enableSearchMobile'),
    [],
  );

  const isEnableTimeFilterMobile = React.useMemo(
    () => SiteConfigManager.getConfig('config.config.sportsbook.enableTimeFilterMobile'),
    [],
  );

  const content = (
    <PrematchSportsbookLoader>
      <ScrollableContainer className={layoutToScrollClass} id="content">
        {!isSportRadarProject && <FreeBetNotification />}

        <SelectedProviderWrapper Component={SelectedSportsbookSportProvider}>
          <Route path={`${sportPrematchPath}/:sportName/:categoryName/:tournamentName`}>
            <SelectedTournamentView />
          </Route>
        </SelectedProviderWrapper>

        <SelectionProvider>
          <Switch>
            <Route exact path={`${sportPrematchPath}/:sportName/:categoryName?`}>
              {isEnableSearchMobile && <StandardSearch />}

              <SportsbookBanner className="sportsbook-banner-wrapper" />

              {areTopEventsAvailable && <TopEvents />}

              <DefaultSportsbookNavigation path={sportPrematchPath}>
                <h1 className="d-none">
                  {locationPage} {locationSportName} {locationCategoryName}
                </h1>
                {isEnableTimeFilterMobile && <SportsTimeFilter />}
                <span className="sport-scroll" />
                <SportsList />
                <div className="sportsbook-content">
                  <SelectedProviderWrapper Component={SelectedSportsbookSportProvider}>
                    <Switch>
                      <Route
                        component={SportsbookFavorites}
                        path={`${sportPrematchPath}/${sportsbookRoutes.favorites}`}
                      />

                      {isCouponsActive && (
                        <Route
                          component={SportsbookSelections}
                          path={`${sportPrematchPath}/${sportsbookRoutes.selections}/:selectionId?`}
                        />
                      )}

                      <EmptySportView>
                        <SportLoader className="absolute">
                          <Route
                            component={Prematch}
                            exact
                            path={`${sportPrematchPath}/:sportName/:categoryName`}
                          />
                          <Route>
                            <SportLoader>
                              <CategoryRedirect />
                              <SportItemDestruction />
                            </SportLoader>
                          </Route>
                        </SportLoader>
                      </EmptySportView>
                    </Switch>
                  </SelectedProviderWrapper>
                </div>
              </DefaultSportsbookNavigation>
            </Route>
            <SelectionsRedirect isRedirect={isSelectionsRedirect} path={sportPrematchPath}>
              <SportRedirect />
            </SelectionsRedirect>
          </Switch>
        </SelectionProvider>
      </ScrollableContainer>
    </PrematchSportsbookLoader>
  );

  return (
    <SelectedEventQueryViewWrapper fallback={content} location={location}>
      <SelectedEventQueryRouteWrapper>
        <DetailsPage />
      </SelectedEventQueryRouteWrapper>
    </SelectedEventQueryViewWrapper>
  );
};

export default PrematchSportsbookView;
